import { MEMBERS, type MemberCode } from '@/types/waterfall'

const BODY_FONT =
  '14px ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
const SMALL_FONT =
  '12px ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'

const CELL_PADDING_X = 24
const MOBILE_CELL_PADDING_X = 12
/** Input cells: td padding + input padding + borders beyond text width. */
const INPUT_CELL_EXTRA = 40
const MOBILE_INPUT_CELL_EXTRA = 28

/** Tighter label column on narrow viewports (below Tailwind `md`). */
export const SPLIT_TABLE_MOBILE_LABEL_COLUMN_SCALE = 1

export interface SplitTableWidths {
  labelPx: number
  valuePx: number
}

function measureText(text: string, font: string): number {
  if (!text) return 0
  if (typeof document === 'undefined') {
    return text.length * 7.5
  }
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return text.length * 7.5
  ctx.font = font
  return ctx.measureText(text).width
}

function maxMeasuredWidth(
  texts: string[],
  font: string,
  extraPadding = 0,
  cellPaddingX = CELL_PADDING_X,
): number {
  const max = texts.reduce((widest, text) => Math.max(widest, measureText(text, font)), 0)
  return Math.ceil(max + cellPaddingX + extraPadding)
}

export function computeSplitTableWidths({
  labelTexts,
  memberTexts,
  totalTexts,
  memberInputTexts = {},
  compact = false,
}: {
  labelTexts: string[]
  memberTexts: Record<MemberCode, string[]>
  totalTexts: string[]
  memberInputTexts?: Partial<Record<MemberCode, string[]>>
  /** Tighter padding for viewports below Tailwind `md`. */
  compact?: boolean
}): SplitTableWidths {
  const cellPaddingX = compact ? MOBILE_CELL_PADDING_X : CELL_PADDING_X
  const inputExtra = compact ? MOBILE_INPUT_CELL_EXTRA : INPUT_CELL_EXTRA

  const labelPx = maxMeasuredWidth(labelTexts, `600 ${BODY_FONT}`, 0, cellPaddingX)

  const allValueTexts = [
    ...MEMBERS.flatMap((member) => memberTexts[member]),
    ...totalTexts,
  ]
  const allInputTexts = MEMBERS.flatMap((member) => memberInputTexts[member] ?? [])

  const valuePx = Math.max(
    maxMeasuredWidth(allValueTexts, `500 tabular-nums ${BODY_FONT}`, 0, cellPaddingX),
    maxMeasuredWidth(allValueTexts, `tabular-nums ${SMALL_FONT}`, 0, cellPaddingX),
    maxMeasuredWidth(allInputTexts, `tabular-nums ${BODY_FONT}`, inputExtra, cellPaddingX),
  )

  return { labelPx, valuePx }
}

export function resolveSplitTableDisplayWidths(
  widths: SplitTableWidths,
  containerWidth: number,
): SplitTableWidths {
  if (containerWidth <= 0) return widths

  const minTableWidth = widths.labelPx + 6 * widths.valuePx
  if (containerWidth <= minTableWidth) return widths

  return {
    labelPx: widths.labelPx,
    valuePx: (containerWidth - widths.labelPx) / 6,
  }
}

export function splitTableMinWidth(widths: SplitTableWidths): number {
  return widths.labelPx + 6 * widths.valuePx
}

/** Longest buildup row labels — used when sizing the sticky label column. */
export const SPLIT_TABLE_BUILDUP_LABELS = [
  'Cash available entering tier',
  'Accrual adjustment (true-up)',
  'Current-period allocation (2× this Tier 1)',
  'Cash cap adjustment',
  'Tier subtotal',
  'Remaining for next tier',
  'Class A accrual adjustment',
  'Class B accrual adjustment',
  'Class A current-period allocation',
  'Class B current-period allocation',
  'Class A cash cap adjustment',
  'Class B cash cap adjustment',
  'Class A subtotal',
  'Class B subtotal',
  'Class A pro-rata allocation',
  'Class B pro-rata allocation',
  'Undistributed remainder',
] as const

export const SPLIT_TABLE_LABEL_COLUMN_SCALE = 1.25
